import DocumentsPane from '@/components/TaskHub/DocumentsPane';
import {Button} from '@/components/ui';
import {Badge} from '@/components/ui/badge';
import {Skeleton} from '@/components/ui/skeleton';
import homePaths from '@/constants/homePaths';
import {WorkflowRunListObject, WorkflowState} from '@/types/workflowRuns';
import {ArrowLeft} from 'lucide-react';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {BrowserRun} from "@/types/index.js";
import linesOfBusiness from "@/constants/linesOfBusiness.js";
import {countLength, formatTitle} from "@/helpers/helperFunctions.js";
import backendApis from "@/services/backendApis.js";
import {toast} from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog.js";

type ApplicationHubSiderProps = {
  application: WorkflowRunListObject | null;
  browserTasks?: BrowserRun[] | null;
  disableBackButton?: boolean;
};

const ApplicationHubSider = (props: ApplicationHubSiderProps) => {
  const {
    application,
    browserTasks,
    disableBackButton = false,
  } = props;

  const nav = useNavigate();

  const totalBrowserRuns = countLength(application?.workflow_run?.browser_run_ids);
  const fetchedRuns = countLength(browserTasks) === totalBrowserRuns || totalBrowserRuns === 0;
  let Lob = browserTasks?.[0]?.business_line;
  if (linesOfBusiness.some(a => a.key === Lob)) {
    Lob = linesOfBusiness.find(a => a.key === Lob)?.name;
  }
  const portals = browserTasks?.map((a) => a?.carrier_name);
  const taskName = application?.workflow_run?.workflow_run_name;

  const goBack = () => {
    nav(homePaths.home, {replace: true});
  };

  const cancelTask = async () => {
    try {
      const params = {
        workflow_run_id: application?.workflow_run?.workflow_run_id,
      }
      const res = await backendApis.cancelWorkflowRun(params);
      if (res.err) {
        throw new Error(res.err);
      }
      toast.success('Application Cancelled', {
        description: "Taking you back to the applications page",
      });
      setTimeout(() => {
        goBack();
      }, 200);
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    }
  }

  return (
    <div className="flex flex-col space-y-4">
      <div className="space-y-3">
        {!disableBackButton && (
          <Button
            className="text-gray-600 hover:text-gray-900"
            onClick={() => goBack()}
            variant="link"
          >
            <ArrowLeft className="mr-2 h-4 w-4"/>
            <span>
              Back to Applications
            </span>
          </Button>
        )}
      </div>
      {application && (
        <>
          <div className="space-y-2">
            <p className="font-medium text-md lg:text-lg pt-1">
              {taskName}
            </p>
            <div className="flex pt-1">
              <p className="w-[80px] text-sm">ID:</p>
              <span className="text-xs italic">
                {application?.workflow_run?.workflow_run_id}
              </span>
            </div>
            <div className="pt-1">
              <p className="text-xs text-gray-700">Line of Business</p>
              <div className="mt-1">
                {fetchedRuns ? (
                  Lob ? (
                    <Badge className="mx-1 bg-gray-100" variant="outline">
                      {formatTitle(Lob)}
                    </Badge>
                  ) : null
                ) : (
                  <Skeleton className="h-6 w-12 bg-gray-300"/>
                )}
              </div>
            </div>
            <div className="pt-1">
              <p className="text-xs text-gray-700">Tasks</p>
              <div className="mt-1">
                {fetchedRuns ? (
                  portals?.map((a) => (
                    <Badge
                      className="mx-1 bg-gray-100"
                      key={a}
                      variant="outline"
                    >
                      {formatTitle(a)}
                    </Badge>
                  ))
                ) : (
                  <Skeleton className="h-6 w-12 bg-gray-300"/>
                )}
              </div>
            </div>
          </div>
          <DocumentsPane run={application.workflow_run}/>
        </>
      )}

      {
        application && ![
          WorkflowState.CANCELLED, WorkflowState.COMPLETED
        ].includes(application?.workflow_context?.workflow_state) && (
          <div>
            <AlertDialog>
              <AlertDialogTrigger>
                <div
                  className='mt-12 text-xs text-red-500 hover:underline '
                >
                  Cancel Application
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Are you sure you want to cancel this application?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={cancelTask}>
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )
      }
    </div>
  );
};

export default ApplicationHubSider;
